import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";

export { ApiError as GrowthChartApiError } from "./api.errors";

// ── Types ──────────────────────────────────────────────────────────────────────
//
// Mirrors app/modules/growth/{enums,standards}.py on the backend. Only
// who_2006 and fenton_2013 have real (draft-placeholder) reference data
// loaded today - the other 7 standards are registered but return a 422
// ("no growth standard with loaded reference data...") until Sid provides
// their source data. See PROGRESS_pediatric_growth_charts.md §10.7.

export type GrowthIndicator =
  | "weight_for_age"
  | "height_for_age"
  | "bmi_for_age"
  | "weight_for_length"
  | "head_circumference_for_age";

export type Standard =
  | "who_2006"
  | "who_2007"
  | "cdc_2000"
  | "cdc_2022_bmi"
  | "iap_2015"
  | "fenton_2013"
  | "fenton_2025"
  | "intergrowth_21st"
  | "down_syndrome";

// Which of the 9 registered standards actually have reference data loaded -
// kept in sync with standards.py's data_available flags by hand for now
// (no "list available standards" endpoint yet). Used to grey out /hide an
// explicit-override picker option rather than let a doctor pick a standard
// that will just 422.
export const STANDARDS_WITH_DATA: ReadonlySet<Standard> = new Set(["who_2006", "fenton_2013"]);

export const STANDARD_LABELS: Record<Standard, string> = {
  who_2006: "WHO 0-5y",
  who_2007: "WHO 5-19y",
  cdc_2000: "CDC 2000",
  cdc_2022_bmi: "CDC 2022 (extended BMI)",
  iap_2015: "IAP 2015",
  fenton_2013: "Fenton 2013 (legacy)",
  fenton_2025: "Fenton 2025",
  intergrowth_21st: "INTERGROWTH-21st",
  down_syndrome: "Down syndrome chart",
};

export type FlagZone = "green" | "amber" | "red" | "out_of_range";

export interface PercentileCurvePoint {
  x: number;
  p3: number;
  p15: number;
  p50: number;
  p85: number;
  p97: number;
  // SD-zone boundaries for background shading - NOT the same z-values as the
  // percentile curves above (p3/p97 sit at ~-1.88/+1.88 SD, not +-2 SD).
  sd_neg3: number;
  sd_neg2: number;
  sd_pos2: number;
  sd_pos3: number;
}

export interface GrowthChartPoint {
  visit_date: string;
  x_value: number;
  value: number;
  // null when flag is "out_of_range" - the point's x-value fell outside the
  // selected standard's valid domain, so no z-score was computed rather
  // than extrapolating a potentially-wrong one.
  z_score: number | null;
  percentile: number | null;
  flag: FlagZone;
}

export interface GrowthChartResponse {
  indicator: GrowthIndicator;
  chart_source: Standard;
  chart_label: string;
  sex: string;
  x_unit: string;
  value_unit: string;
  reference_curves: PercentileCurvePoint[];
  patient_series: GrowthChartPoint[];
  is_draft_reference_data: boolean;
}

export interface GrowthVelocityPoint {
  period_start: string;
  period_end: string;
  weight_velocity_kg_per_month: number | null;
  height_velocity_cm_per_month: number | null;
}

export interface GrowthTrendResponse {
  chart_label: string;
  velocity: GrowthVelocityPoint[];
}

export interface GrowthChartShareRequest {
  emergency_contact_id: string;
  indicator: GrowthIndicator;
  message: string;
  chart_image_base64: string;
}

export interface GrowthChartShareResponse {
  queued: boolean;
  notification_id: string | null;
  sent_to_masked: string;
}

// ── API ────────────────────────────────────────────────────────────────────────

export const GrowthChartAPI = {
  get(patientId: string, indicator: GrowthIndicator, standard?: Standard): Promise<GrowthChartResponse> {
    const params = new URLSearchParams({ indicator });
    if (standard) params.set("standard", standard);
    return apiRequest<GrowthChartResponse>(`${ROUTES.GROWTH_CHART(patientId)}?${params.toString()}`);
  },

  getTrend(patientId: string): Promise<GrowthTrendResponse> {
    return apiRequest<GrowthTrendResponse>(ROUTES.GROWTH_CHART_TREND(patientId));
  },

  share(patientId: string, data: GrowthChartShareRequest): Promise<GrowthChartShareResponse> {
    return apiRequest<GrowthChartShareResponse>(ROUTES.GROWTH_CHART_SHARE(patientId), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
};
